import { useState } from "react";

const STEPS = [
  { key: "apply", label: "提交申请", icon: "ri-file-add-line", desc: "用户提交报装申请，上传相关证件材料" },
  { key: "review", label: "资料审核", icon: "ri-file-search-line", desc: "工作人员审核申请材料，核实用户信息" },
  { key: "report", label: "报备登记", icon: "ri-government-line", desc: "向主管部门报备，获取施工许可" },
  { key: "survey", label: "现场勘查", icon: "ri-map-pin-user-line", desc: "技术人员上门勘查，制定施工方案" },
  { key: "construct", label: "施工安装", icon: "ri-tools-line", desc: "专业施工队伍进行管道安装和设备安装" },
  { key: "accept", label: "竣工验收", icon: "ri-checkbox-circle-line", desc: "质检人员验收施工质量，签署验收报告" },
  { key: "open", label: "开户通气", icon: "ri-fire-line", desc: "建立用户档案，开通燃气供应，完成开户" },
];

interface InstallFlowProps {
  currentStep: number;
  appId: string;
  applicant: string;
}

export default function InstallFlow({ currentStep, appId, applicant }: InstallFlowProps) {
  const [activeStep, setActiveStep] = useState(currentStep);
  const [uploadModal, setUploadModal] = useState(false);
  const [uploadStep, setUploadStep] = useState(0);

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold text-gray-800">报装流程进度</h3>
          <p className="text-xs text-gray-400 mt-0.5">{appId} · {applicant}</p>
        </div>
        <button
          onClick={() => setUploadModal(true)}
          className="flex items-center gap-1.5 text-xs text-[#E8531A] border border-[#E8531A]/30 px-3 py-1.5 rounded-lg cursor-pointer hover:bg-orange-50 whitespace-nowrap"
        >
          <i className="ri-upload-line"></i>上传文件
        </button>
      </div>

      {/* Step Flow */}
      <div className="relative">
        {/* Connector line */}
        <div className="absolute top-5 left-5 right-5 h-0.5 bg-gray-100 z-0">
          <div
            className="h-full bg-[#E8531A] transition-all duration-500"
            style={{ width: `${(activeStep / (STEPS.length - 1)) * 100}%` }}
          />
        </div>
        <div className="flex justify-between relative z-10">
          {STEPS.map((step, i) => {
            const isDone = i < activeStep;
            const isCurrent = i === activeStep;
            return (
              <div
                key={step.key}
                className="flex flex-col items-center cursor-pointer"
                onClick={() => setActiveStep(i)}
              >
                <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all ${
                  isDone ? "bg-green-500 border-green-500" :
                  isCurrent ? "bg-[#E8531A] border-[#E8531A]" :
                  "bg-white border-gray-200"
                }`}>
                  {isDone ? (
                    <i className="ri-check-line text-white text-sm"></i>
                  ) : (
                    <i className={`${step.icon} text-sm ${isCurrent ? "text-white" : "text-gray-400"}`}></i>
                  )}
                </div>
                <span className={`text-[10px] mt-1.5 whitespace-nowrap font-medium ${
                  isDone ? "text-green-600" : isCurrent ? "text-[#E8531A]" : "text-gray-400"
                }`}>{step.label}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Current Step Detail */}
      <div className="mt-4 bg-orange-50 rounded-xl p-4 border border-orange-100">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-6 h-6 flex items-center justify-center rounded-lg bg-[#E8531A]/20">
            <i className={`${STEPS[activeStep].icon} text-[#E8531A] text-sm`}></i>
          </div>
          <span className="text-sm font-semibold text-gray-800">
            当前步骤：{STEPS[activeStep].label}
          </span>
          <span className="text-xs text-gray-400">（第{activeStep + 1}/{STEPS.length}步）</span>
        </div>
        <p className="text-xs text-gray-600 mb-3">{STEPS[activeStep].desc}</p>
        <div className="flex gap-2">
          {activeStep < STEPS.length - 1 && (
            <button
              onClick={() => setActiveStep(s => Math.min(s + 1, STEPS.length - 1))}
              className="flex items-center gap-1.5 text-xs bg-[#E8531A] text-white px-3 py-1.5 rounded-lg cursor-pointer hover:bg-[#d44a15] whitespace-nowrap"
            >
              <i className="ri-arrow-right-line"></i>推进到下一步
            </button>
          )}
          {activeStep === STEPS.length - 1 && (
            <button className="flex items-center gap-1.5 text-xs bg-green-500 text-white px-3 py-1.5 rounded-lg cursor-pointer hover:bg-green-600 whitespace-nowrap">
              <i className="ri-check-double-line"></i>完成开户
            </button>
          )}
          <button
            onClick={() => setUploadModal(true)}
            className="flex items-center gap-1.5 text-xs border border-gray-200 text-gray-600 px-3 py-1.5 rounded-lg cursor-pointer hover:bg-gray-50 whitespace-nowrap"
          >
            <i className="ri-attachment-line"></i>上传附件
          </button>
        </div>
      </div>

      {/* File Upload Modal */}
      {uploadModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setUploadModal(false)}>
          <div className="bg-white rounded-2xl p-6 w-[440px]" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold text-gray-900">上传业务文件</h3>
              <button onClick={() => setUploadModal(false)} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 cursor-pointer">
                <i className="ri-close-line text-gray-500"></i>
              </button>
            </div>
            <div className="space-y-3">
              {[
                { label: "身份证明", required: true, uploaded: true },
                { label: "房产证明", required: true, uploaded: true },
                { label: "施工方案图纸", required: false, uploaded: false },
                { label: "验收报告", required: false, uploaded: false },
                { label: "开户申请表", required: true, uploaded: false },
              ].map((file, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <i className={`${file.uploaded ? "ri-file-check-line text-green-500" : "ri-file-line text-gray-400"} text-sm`}></i>
                    <span className="text-sm text-gray-700">{file.label}</span>
                    {file.required && <span className="text-xs text-red-400">*必填</span>}
                  </div>
                  {file.uploaded ? (
                    <span className="text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full">已上传</span>
                  ) : (
                    <button className="text-xs text-[#E8531A] border border-[#E8531A]/30 px-2 py-1 rounded-lg cursor-pointer hover:bg-orange-50 whitespace-nowrap">
                      上传
                    </button>
                  )}
                </div>
              ))}
            </div>
            <button onClick={() => setUploadModal(false)} className="w-full mt-4 bg-[#E8531A] text-white py-2.5 rounded-lg text-sm cursor-pointer hover:bg-[#d44a15] whitespace-nowrap">
              确认
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
